import { useEffect, useMemo, useState } from 'react';
import { formatUtcDateTimeToTimeZone } from '@belong/common';
import { IconClose, IconLeftCaret, IconRightCaret } from '@belong/icons';
import { Text } from '@belong/ui';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  type Availability,
  type RangeKey,
  TIME_RANGES,
  formatDateOnly,
} from './availability-utils';

export type { Availability };

interface AvailabilityPickerProps {
  timeZone?: string;
  initialSelections?: Availability[];
  onChange?: (selections: Availability[]) => void;
}

const RANGE_KEYS: RangeKey[] = ['AllDay', 'Morning', 'Afternoon', 'Evening'];
const AMOUNT_OF_DAYS = 15;
const MIN_SELECTIONS = 3;
const MAX_SELECTIONS = 10;
const DATES_PER_PAGE = 4;
const MOBILE_DATES_PER_PAGE = 2;

function formatHour(hour: number) {
  if (hour === 0) return '12AM';
  if (hour < 12) return `${hour}AM`;
  if (hour === 12) return 'Noon';
  return `${hour - 12}PM`;
}

function formatTimeRange(startHour: number, endHour: number) {
  return `${formatHour(startHour)} - ${formatHour(endHour)}`;
}

function mergeTimeRanges(times: RangeKey[]) {
  if (times.includes('AllDay')) return 'All Day';

  const sorted = [...times].sort(
    (a, b) => TIME_RANGES[a].startHour - TIME_RANGES[b].startHour,
  );
  const first = sorted[0];
  if (!first) return '';

  const merged: string[] = [];
  let currentStart = TIME_RANGES[first].startHour;
  let currentEnd = TIME_RANGES[first].endHour;

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]!;
    const nextStart = TIME_RANGES[next].startHour;
    const nextEnd = TIME_RANGES[next].endHour;

    if (nextStart === currentEnd) {
      currentEnd = nextEnd;
    } else {
      merged.push(formatTimeRange(currentStart, currentEnd));
      currentStart = nextStart;
      currentEnd = nextEnd;
    }
  }

  merged.push(formatTimeRange(currentStart, currentEnd));
  return merged.join(' | ');
}

function generateDates(timeZone: string) {
  const todayInTz = utcToZonedTime(new Date(), timeZone);
  const dates: string[] = [];
  for (let i = 0; i < AMOUNT_OF_DAYS; i++) {
    const d = new Date(
      todayInTz.getFullYear(),
      todayInTz.getMonth(),
      todayInTz.getDate() + i,
    );
    dates.push(format(d, 'yyyy-MM-dd'));
  }
  return dates;
}

export function AvailabilityPicker({
  timeZone = 'America/Los_Angeles',
  initialSelections = [],
  onChange,
}: AvailabilityPickerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [selections, setSelections] =
    useState<Availability[]>(initialSelections);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const dates = useMemo(() => generateDates(timeZone), [timeZone]);

  const pages = useMemo(() => {
    const perPage = isMobile ? MOBILE_DATES_PER_PAGE : DATES_PER_PAGE;
    const result: string[][] = [];
    for (let i = 0; i < dates.length; i += perPage) {
      result.push(dates.slice(i, i + perPage));
    }
    return result;
  }, [dates, isMobile]);

  const isLeftDisabled = currentPage === 0;
  const isRightDisabled = currentPage === pages.length - 1;

  const emitChange = (next: Availability[]) => {
    setSelections(next);
    onChange?.(next);
  };

  const handleDateSelect = (dateStr: string) => {
    const dateAlreadyInSelections = selections.some((s) => s.date === dateStr);
    if (selections.length >= MAX_SELECTIONS && !dateAlreadyInSelections) return;
    setSelectedDate((prev) => (prev === dateStr ? null : dateStr));
  };

  const handleTimeRangeSelect = (dateStr: string, range: RangeKey) => {
    const existing = selections.find((s) => s.date === dateStr);

    if (existing) {
      const hasRange = existing.times.includes(range);
      let newTimes: RangeKey[];
      if (hasRange) {
        newTimes = existing.times.filter((r) => r !== range);
      } else if (range === 'AllDay') {
        newTimes = ['AllDay'];
      } else {
        newTimes = [...existing.times.filter((r) => r !== 'AllDay'), range];
      }

      if (newTimes.length === 0) {
        emitChange(selections.filter((s) => s.date !== dateStr));
      } else {
        emitChange(
          selections.map((s) =>
            s.date === dateStr ? { ...s, times: newTimes } : s,
          ),
        );
      }
      return;
    }

    if (selections.length >= MAX_SELECTIONS) return;
    emitChange([...selections, { date: dateStr, times: [range] }]);
  };

  const handleRemoveDate = (dateStr: string) => {
    emitChange(selections.filter((s) => s.date !== dateStr));
  };

  return (
    <div className="w-full">
      <div className="mb-lg flex flex-col md:flex-row md:items-center justify-between">
        <Text variant="h3" fontWeight="semibold" className="text-navy">
          Select 3 Date & Times
        </Text>
        <div className="flex space-x-xs mt-xs md:mt-0">
          {Array.from({ length: MIN_SELECTIONS }, (_, i) => (
            <div
              key={i}
              style={{ width: 18, height: 4 }}
              className={clsx(
                'rounded-full',
                i < selections.length ? 'bg-green' : 'bg-gray',
              )}
            />
          ))}
        </div>
      </div>

      <div className="relative">
        <button
          type="button"
          className={clsx(
            'absolute -top-sm left-0 z-10 flex h-sm w-sm items-center justify-center md:top-1/2 md:mt-0 md:-translate-y-1/2',
            isLeftDisabled
              ? 'cursor-not-allowed text-gray'
              : 'hover:text-dark-navy',
          )}
          onClick={() => {
            if (!isLeftDisabled) {
              setCurrentPage((p) => p - 1);
            }
          }}
          disabled={isLeftDisabled}
        >
          <IconLeftCaret className="h-sm w-sm" />
        </button>

        <div className="overflow-hidden px-2xs md:px-lg">
          <div className="mb-md grid grid-cols-2 gap-sm md:grid-cols-4 md:gap-md">
            {pages[currentPage]?.map((dateStr) => {
              const dateSelection = selections.find((s) => s.date === dateStr);
              return (
                <DateCard
                  key={dateStr}
                  dateStr={dateStr}
                  isSelected={selectedDate === dateStr}
                  hasSelections={!!dateSelection}
                  selectionsCount={dateSelection?.times.length ?? 0}
                  isDisabled={
                    selections.length >= MAX_SELECTIONS && !dateSelection
                  }
                  onDateSelect={handleDateSelect}
                />
              );
            })}
          </div>

          {selectedDate && pages[currentPage]?.includes(selectedDate) && (
            <div className="grid grid-cols-2 gap-sm md:grid-cols-4 pb-sm">
              {RANGE_KEYS.map((rangeKey) => {
                const dateSelection = selections.find(
                  (s) => s.date === selectedDate,
                );
                const isAtMax = selections.length >= MAX_SELECTIONS;
                const isThisRangeSelected =
                  !!dateSelection?.times.includes(rangeKey);
                const isDisabled = isAtMax && !dateSelection;

                return (
                  <button
                    key={rangeKey}
                    type="button"
                    style={{ height: isMobile ? 70 : 110 }}
                    className={clsx(
                      'w-full rounded border p-sm text-center transition-colors md:p-sm',
                      isThisRangeSelected
                        ? 'border-navy bg-navy text-white duration-hover border-2 border-solid border-transparent hover:border-dark-navy'
                        : isDisabled
                          ? 'cursor-not-allowed border-solid border-gray bg-light-gray text-dark-gray'
                          : 'bg-white text-navy shadow duration-hover border-2 border-solid border-transparent hover:border-dark-navy',
                    )}
                    onClick={() => {
                      if (isDisabled) return;
                      handleTimeRangeSelect(selectedDate, rangeKey);
                    }}
                    disabled={isDisabled}
                  >
                    <Text fontWeight="semibold" className="mb-0 leading-p2">
                      {TIME_RANGES[rangeKey].label}
                    </Text>
                    <Text className="text-p1">
                      {TIME_RANGES[rangeKey].timeRange}
                    </Text>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className={clsx(
            'absolute -top-sm right-0 z-10 flex h-sm w-sm items-center justify-center md:top-1/2 md:mt-0 md:-translate-y-1/2',
            isRightDisabled
              ? 'cursor-not-allowed text-gray'
              : 'hover:text-dark-navy',
          )}
          onClick={() => {
            if (!isRightDisabled) {
              setCurrentPage((p) => p + 1);
            }
          }}
          disabled={isRightDisabled}
        >
          <IconRightCaret className="h-sm w-sm" />
        </button>
      </div>

      <Text className="text-sm mt-2xs text-right text-dark-gray">
        {`All timezones in ${formatUtcDateTimeToTimeZone({
          dateTime: new Date(),
          format: 'z',
          timeZone,
        })}.`}
      </Text>

      {selections.length > 0 && (
        <div className="mt-xl pb-sm">
          <div className="space-y-sm">
            {selections.map((sel) => {
              const dateLabel = formatDateOnly(sel.date, 'EEEE');
              const dateSubLabel = formatDateOnly(sel.date, 'MMM do, yyyy');
              const timeRanges = mergeTimeRanges(sel.times);

              return (
                <div
                  key={sel.date}
                  className="flex items-start justify-between px-2xs"
                >
                  <div className="flex flex-col md:flex-row">
                    <Text fontWeight="semibold" className="text-body">
                      {`${dateLabel}, ${dateSubLabel}`}
                    </Text>
                    <Text fontWeight="semibold" className="text-body md:ml-xs">
                      ({timeRanges})
                    </Text>
                  </div>
                  <button
                    type="button"
                    className="mt-xs text-gray hover:text-dark-gray"
                    onClick={() => handleRemoveDate(sel.date)}
                  >
                    <IconClose
                      width={16}
                      className="text-navy hover:text-dark-navy"
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

interface DateCardProps {
  dateStr: string;
  isSelected: boolean;
  hasSelections: boolean;
  selectionsCount: number;
  isDisabled?: boolean;
  onDateSelect: (dateStr: string) => void;
}

function DateCard({
  dateStr,
  isSelected,
  hasSelections,
  selectionsCount,
  isDisabled = false,
  onDateSelect,
}: DateCardProps) {
  const dateLabel = formatDateOnly(dateStr, 'EEEE');
  const dateSubLabel = formatDateOnly(dateStr, 'MMM do, yyyy');

  return (
    <button
      type="button"
      className={clsx(
        'mt-sm flex min-w-0 flex-1 flex-col justify-start rounded p-sm text-left shadow transition-colors duration-hover border border-solid border-transparent hover:border-dark-navy',
        {
          'cursor-not-allowed opacity-50': isDisabled,
          'cursor-pointer': !isDisabled,
          'hover:border-navy': !isSelected,
          'border-navy bg-navy text-white hover:border-dark-navy': isSelected,
          'text-navy border-white': !isSelected && !hasSelections,
          'border-white bg-white text-navy': !isSelected && hasSelections,
        },
      )}
      style={{ height: 110 }}
      disabled={isDisabled}
      onClick={() => onDateSelect(dateStr)}
    >
      <div className="flex h-full w-full flex-col justify-center text-center relative">
        <div className="md:mb-xs">
          <Text
            variant="body"
            fontWeight="semibold"
            className={clsx('text-sm', isSelected ? 'text-white' : 'text-navy')}
          >
            {dateLabel}
          </Text>
          <Text
            variant="p2"
            className={clsx(isSelected ? 'text-white' : 'text-navy')}
          >
            {dateSubLabel}
          </Text>
        </div>
        <div
          className="flex justify-center space-x-2xs absolute bottom-0 inset-x-0"
          style={{ height: 6 }}
        >
          {selectionsCount > 0 ? (
            Array.from({ length: selectionsCount }, (_, i) => (
              <div
                key={i}
                style={{ width: 6, height: 6 }}
                className="rounded-full bg-green"
              />
            ))
          ) : (
            <div style={{ width: 6, height: 6 }} />
          )}
        </div>
      </div>
    </button>
  );
}
